import { CellContext } from '@tanstack/react-table';
import { getTableMeta, UnallocatedCampaignsRow } from '../../helpers';
import { Skeleton } from '@round/ui-kit';
import { creatorbase } from '@round/api';
import { useUpdateCampaign } from '@round/creatorbase-helpers/api-hooks';
import { showNotification } from 'helpers';
import { TooltipOnOverflow } from '@round/whitelabel-ui';
import { useEffect, useState } from 'react';
import styles from './NotesCell.module.css';
import { TableTextarea } from '@round/whitelabel-ui';

type Context = CellContext<UnallocatedCampaignsRow, 'notes'>;

const NotesCell = ({ row, table, getValue }: Context) => {
    const value = getValue();
    const { update, isLoading: isUpdateLoading } = useUpdateCampaign(row.original.id);
    const { isLoading, isRefetching, onUpdated } = getTableMeta(table);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [notes, setNotes] = useState<string>(value || '');

    useEffect(() => {
        setNotes(value || '');
    }, [value]);

    const handleUpdateCampaign = (updates: Partial<creatorbase.PatchCampaignBody>) => {
        update(
            { id: row.original.id, ...updates },
            {
                onSuccess: (data) => {
                    setNotes(data.notes);
                    onUpdated(data);
                    showNotification('Notes updated', 'info');
                },
                onError: () => {
                    setNotes(getValue());
                    showNotification('Error updating notes', 'error');
                },
            }
        );
    };

    if (isLoading || Object.keys(row.original).length === 0) {
        return <Skeleton width="10rem" />;
    }

    return (
        <TooltipOnOverflow tooltip={!notes || isUpdateLoading ? undefined : notes} disabled={isInputFocused}>
            <TableTextarea
                value={isInputFocused ? notes : notes || '-'}
                onChange={(e) => setNotes(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                    }
                }}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => {
                    if (notes !== value) {
                        handleUpdateCampaign({ notes: notes });
                    }
                    setIsInputFocused(false);
                }}
                className={styles.input}
                disabled={isRefetching}
                rows={2}
            />
        </TooltipOnOverflow>
    );
};
export default NotesCell;
